import { Reducer } from 'react';

export interface SmartShareState {
  open: boolean;
  buttonHovered: boolean;
  currentStep: number;
  generationTone: string;
  useCustomDescription: string;
  includeHashtags: boolean;
  includeKeyPoints: boolean;
  summaryLength: string;
  generationJobDescription: string;
  generationJobTitle: string;
  adType: string;
  generatedResponse: string;
  generatedKeyPoints: string[];
  copied: boolean;
  confirmCancelIsOpen: boolean;
  generationType: string;
  generationError: boolean;
  refinedResponse: string;
  isLoading: boolean;
}

export const initialSmartShareState: SmartShareState = {
  open: false,
  buttonHovered: false,
  currentStep: 0,
  generationTone: 'excited',
  useCustomDescription: 'description',
  includeHashtags: false,
  includeKeyPoints: false,
  summaryLength: 'medium',
  generationJobDescription: '',
  generationJobTitle: '',
  adType: 'dot points',
  generatedResponse: '',
  generatedKeyPoints: [],
  copied: false,
  confirmCancelIsOpen: false,
  generationType: 'social',
  generationError: false,
  refinedResponse: '',
  isLoading: false
};

export type SmartShareAction =
  | { type: 'OPEN_MODAL'; payload: string }
  | { type: 'CLOSE_MODAL' }
  | { type: 'SET_CONFIRM_CANCEL_IS_OPEN'; payload: boolean }
  | { type: 'SET_CURRENT_STEP'; payload: number }
  | { type: 'SET_COPIED'; payload: boolean }
  | { type: 'SET_GENERATED_RESPONSE'; payload: string }
  | { type: 'SET_GENERATED_KEY_POINTS'; payload: string[] }
  | { type: 'SET_GENERATION_ERROR'; payload: boolean }
  | { type: 'SET_BUTTON_HOVERED'; payload: boolean }
  | { type: 'SELECT_SOCIAL' }
  | { type: 'SELECT_JOB_AD' }
  | { type: 'SET_USE_CUSTOM_DESCRIPTION'; payload: string }
  | { type: 'SET_GENERATION_JOB_TITLE'; payload: string }
  | { type: 'SET_GENERATION_JOB_DESCRIPTION'; payload: string }
  | { type: 'SET_GENERATION_TONE'; payload: string }
  | { type: 'SET_SUMMARY_LENGTH'; payload: string }
  | { type: 'SET_INCLUDE_HASHTAGS'; payload: boolean }
  | { type: 'SET_INCLUDE_KEY_POINTS'; payload: boolean }
  | { type: 'SET_AD_TYPE'; payload: string }
  | { type: 'SET_REFINED_RESPONSE'; payload: string }
  | {
      type: 'SET_LOADING_AND_STEP';
      payload: { isLoading: boolean; currentStep?: number; hasFeedbackInput?: boolean };
    };

export const smartShareReducer: Reducer<SmartShareState, SmartShareAction> = (
  state: SmartShareState,
  action: SmartShareAction
) => {
  switch (action.type) {
    case 'OPEN_MODAL':
      return { ...state, open: true, generationJobTitle: action.payload };
    case 'CLOSE_MODAL':
      return initialSmartShareState;
    case 'SET_CONFIRM_CANCEL_IS_OPEN':
      return { ...state, confirmCancelIsOpen: action.payload };
    case 'SET_COPIED':
      return { ...state, copied: action.payload };
    case 'SET_CURRENT_STEP':
      return { ...state, currentStep: action.payload };
    case 'SET_GENERATED_RESPONSE':
      return { ...state, generatedResponse: action.payload };
    case 'SET_GENERATED_KEY_POINTS':
      return { ...state, generatedKeyPoints: action.payload };
    case 'SET_GENERATION_ERROR':
      return { ...state, generationError: action.payload };
    case 'SET_BUTTON_HOVERED':
      return { ...state, buttonHovered: action.payload };
    case 'SELECT_SOCIAL':
      return { ...state, generationType: 'social', generationTone: 'excited', currentStep: 1 };
    case 'SELECT_JOB_AD':
      return {
        ...state,
        generationType: 'advertisement',
        generationTone: 'excited',
        currentStep: 4
      };
    case 'SET_USE_CUSTOM_DESCRIPTION':
      return { ...state, useCustomDescription: action.payload };
    case 'SET_GENERATION_JOB_DESCRIPTION':
      return { ...state, generationJobDescription: action.payload };
    case 'SET_GENERATION_JOB_TITLE':
      return { ...state, generationJobTitle: action.payload };
    case 'SET_GENERATION_TONE':
      return { ...state, generationTone: action.payload };
    case 'SET_SUMMARY_LENGTH':
      return { ...state, summaryLength: action.payload };
    case 'SET_INCLUDE_HASHTAGS':
      return { ...state, includeHashtags: action.payload };
    case 'SET_INCLUDE_KEY_POINTS':
      return { ...state, includeKeyPoints: action.payload };
    case 'SET_AD_TYPE':
      return { ...state, adType: action.payload };
    case 'SET_REFINED_RESPONSE':
      return { ...state, refinedResponse: action.payload };
    case 'SET_LOADING_AND_STEP':
      return {
        ...state,
        isLoading: action.payload.isLoading,
        ...(action.payload.currentStep !== undefined && {
          currentStep: action.payload.currentStep
        }),
        ...(action.payload.hasFeedbackInput !== undefined && {
          hasFeedbackInput: action.payload.hasFeedbackInput
        })
      };
    default:
      return state;
  }
};
